// 顯示用格式（日期、分組標題、相對時間、圖片網址）。字串一律來自 i18n。
import type { ReleaseDate } from '@dayone/core/release-date';
import { epochDay, fromEpochDay, weekday } from '@dayone/core/time';
import type { SectionHeader } from '../filters/catalog.ts';
import { t } from '../i18n/zh-TW.ts';

export function formatRelease(d: ReleaseDate, currentYear: number): string {
  switch (d.p) {
    case 'day':
      return t.date.day(d.y, d.m, d.d, t.date.weekday(weekday(epochDay(d.y, d.m, d.d))), d.y !== currentYear);
    case 'month':
      return t.date.month(d.y, d.m);
    case 'quarter':
      return t.date.quarter(d.y, d.q);
    case 'year':
      return t.date.year(d.y);
    case 'tba':
      return t.date.tba;
  }
}

/** 段落標題的文字：大字（數字或短字）、標籤、副標、完整名稱 */
export function sectionText(h: Pick<SectionHeader, 'section' | 'exact' | 'fuzzy'>): {
  big: string;
  label: string;
  sub: string;
  full: string;
} {
  const s = h.section;
  switch (s.kind) {
    case 'week':
    case 'next-week': {
      const md = (day: number) => {
        const { m, d } = fromEpochDay(day);
        return `${m}/${d}`;
      };
      const range = t.group.range(md(s.start), md(s.end));
      const label = s.kind === 'week' ? t.group.week : t.group.nextWeek;
      const full = s.kind === 'week' ? t.group.full.week(range) : t.group.full.nextWeek(range);
      return { big: md(s.start), label, sub: range, full };
    }
    case 'month':
      return {
        big: String(s.m),
        label: t.group.monthLabel(s.m),
        sub: t.group.monthSub(s.y, h.exact, h.fuzzy),
        full: t.group.full.month(s.y, s.m),
      };
    case 'quarter':
      return {
        big: `Q${s.q}`,
        label: t.group.quarterLabel(s.y, s.q),
        sub: t.group.undated,
        full: t.group.full.quarter(s.y, s.q),
      };
    case 'year':
      return { big: String(s.y), label: t.group.yearLabel, sub: t.group.undated, full: t.group.full.year(s.y) };
    case 'tba':
      return { big: 'TBA', label: t.group.tbaLabel, sub: t.group.tbaSub, full: t.group.full.tba };
    case 'overdue':
      return { big: '?', label: t.group.overdueLabel, sub: t.group.overdueSub, full: t.group.full.overdue };
  }
}

/** 倒數：0 → 今天發售、1 → 明天發售、n → n 天後發售 */
export function countdown(days: number): { value: string; unit: string } {
  if (days <= 0) return { value: '0', unit: t.countdown.today };
  if (days === 1) return { value: '1', unit: t.countdown.tomorrow };
  return { value: String(days), unit: t.countdown.days };
}

export function timeAgo(iso: string | undefined, now: number): string | null {
  if (!iso) return null;
  const diff = now - Date.parse(iso);
  if (Number.isNaN(diff)) return null;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return t.freshness.ago.justNow;
  if (min < 60) return t.freshness.ago.minutes(min);
  const h = Math.floor(min / 60);
  if (h < 24) return t.freshness.ago.hours(h);
  return t.freshness.ago.days(Math.floor(h / 24));
}

/** IGDB 圖片：參照格式 `igdb:<image_id>`；其他 https 網址原樣使用 */
export function imageUrl(
  ref: string | undefined,
  size: 'cover_small' | 'cover_big' | 'screenshot_med' | 'screenshot_big' | 'screenshot_huge' | '720p' | '1080p',
): string | null {
  if (!ref) return null;
  if (ref.startsWith('igdb:')) return `https://images.igdb.com/igdb/image/upload/t_${size}/${ref.slice(5)}.webp`;
  return ref.startsWith('https://') ? ref : null;
}

/** 由字串產生穩定的色相，封面載入失敗時的佔位圖用 */
export function hueOf(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}
